import type { TaxLineType } from '@/types/tax';

/**
 * How the tax engine classifies a line, in the one spelling it understands.
 *
 * Shared rather than restated per form. The pricebook and the estimate editor
 * are two ways of answering the SAME question about the same field, and when
 * each kept its own list they drifted: the pricebook create form's list was
 * missing `freight`, and the invoice editor still offers an upper-case
 * vocabulary (`LABOR`, `MATERIAL`, `FLAT`) that only survives because the
 * server normalizes it on the way in. There is one set of values; this is it.
 */
export const TAX_LINE_TYPE_OPTIONS: ReadonlyArray<{ value: TaxLineType; label: string }> = [
  { value: 'flat_rate', label: 'Flat Rate' },
  { value: 'labor', label: 'Labor' },
  { value: 'materials', label: 'Materials' },
  { value: 'freight', label: 'Freight' },
  { value: 'exempt', label: 'Exempt' },
];

/**
 * The sentinel a Select uses for "no explicit override". Not a TaxLineType:
 * leaving the field empty is what lets the server derive one from the line's
 * type, and storing a derived value would freeze today's derivation into the
 * document forever.
 */
export const AUTO_TAX_LINE_TYPE = '__AUTO_TAX_LINE_TYPE__';

export const taxLineTypeLabel = (value: TaxLineType | string): string =>
  TAX_LINE_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value;

/**
 * The two answers a DOCUMENT line may still state for itself.
 *
 * Everything else is derived from what the line is — see
 * `resolveLineTaxLineType` in `@/lib/tax/tax-utils` and its twin in the
 * operations pricing engine. A stored `labor`, `materials` or `flat_rate` is
 * read by neither: letting a snapshot decide its own tax is what silently
 * under-collected on materials carrying `labor` and over-collected on services
 * carrying `materials`.
 *
 * `exempt` is here because the engine honours it, but a document line expresses
 * it by unticking Taxable rather than by picking it.
 */
export const OVERRIDABLE_TAX_LINE_TYPES: ReadonlyArray<TaxLineType> = ['freight', 'exempt'];

/**
 * Whether a stored value is one the reader will actually honour.
 *
 * Anything else — `labor`, `materials`, `flat_rate`, a blank, junk — is derived
 * over, so a selector shows it as "Auto" rather than echoing an override that
 * does not exist.
 */
export function isOverridableTaxLineType(value: unknown): boolean {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase() as TaxLineType;
  return OVERRIDABLE_TAX_LINE_TYPES.includes(normalized);
}

/**
 * What a selector should BIND to for a stored value: the class itself when a
 * reader will honour it, otherwise the Auto sentinel.
 *
 * Returns the NORMALIZED spelling, which is the whole point of having it.
 * `isOverridableTaxLineType` accepts `FREIGHT` and ` Exempt ` on purpose, so a
 * caller that tested with it and then bound the raw value handed the Select a
 * value matching none of its options — the trigger fell blank and the label
 * printed the raw spelling. One call site normalized and the other did not;
 * this is the version neither can get wrong.
 */
export function selectableTaxLineType(value: unknown): string {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();
  return isOverridableTaxLineType(normalized) ? normalized : AUTO_TAX_LINE_TYPE;
}

/**
 * What a form should STORE for a value: the normalized class when a reader will
 * honour it, otherwise nothing.
 *
 * The submit-side counterpart to `selectableTaxLineType`, and the reason both
 * exist: a form that showed "Auto" for a derived `labor` while still posting
 * `labor` would persist an override it had just told the operator was not
 * there. Sending nothing lets the server derive, which lands on the same class
 * for an unchanged type — and on the RIGHT one when the type changed, which is
 * exactly where re-sending the old value did damage.
 */
export function storableTaxLineType(value: unknown): TaxLineType | undefined {
  const selected = selectableTaxLineType(value);
  return selected === AUTO_TAX_LINE_TYPE ? undefined : (selected as TaxLineType);
}

/**
 * What the engine will derive for a line that states nothing — a mirror of
 * `resolveLineTaxLineType`, which is the only thing that decides.
 *
 * Accepts either spelling of the type. The pricebook form works in lower-case
 * item types (`service`) and a document line in upper-case ones (`SERVICE`),
 * and a helper that quietly returned the default for one of them would show
 * the operator a confident, wrong "Auto → …" on every service line.
 */
export function resolveAutoTaxLineType(itemType: string, taxable: boolean): TaxLineType {
  if (!taxable) return 'exempt';
  switch (itemType.toUpperCase()) {
    case 'MATERIAL':
    case 'EQUIPMENT':
      return 'materials';
    case 'MEMBERSHIP':
      return 'exempt';
    // SERVICE and BUNDLE both sell work, and so does an unrecognised type —
    // which is where `flat_rate` used to land, and it matched exactly the codes
    // `labor` matches. A FEE lands here deliberately rather than by falling
    // through: a trip or dispatch charge buys the visit the work happens on,
    // and no code can apply to `flat_rate` alone, so `flat_rate` would name a
    // regime the engine cannot charge in. Mirrors the engine's own default
    // rather than the older `flat_rate`, so "Auto → …" cannot promise a class
    // the engine will not use.
    default:
      return 'labor';
  }
}
